<script setup lang="ts">
import { computed, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { AkButton, AkIcon, AkModal } from '@ankorstore/design-system';
import { useI18n } from 'vue-i18n';
import QuantityCounter from '../quantity-counter/quantity-counter.vue';
import VatForm from '@monolith/legacy/components/vat-form.vue';
import LoaderIcon from '@monolith/legacy/components/loader-icon.vue';
import VariantForm from './variant-form.vue';
import type Product from '@monolith/legacy/types/product';
import type { Variant } from '@monolith/legacy/types/product';
import type { ESProductHit } from '@bc/discovery/types';
import { useAuthPopin } from '@core/composables/use-auth-popin';
import { RetailerFormType } from '@monolith/legacy/types/retailer-form-type';
import { useAddToCart, type AddToCartMode } from './composables/use-add-to-cart';
import QatcSingleVariant from './qatc-single-variant.vue';
import QatcMultiVariant from './qatc-multi-variant.vue';
import { useFlyToCart } from '@bc/shared/composables/use-fly-to-cart';
import { useCart } from '@monolith/legacy/modules/cart-2/composables/use-cart';
import { calculateTotalPrice } from '@monolith/legacy/services/product-variants';
import type { PriceBreakdown } from '@monolith/legacy/types/product';
import usePrice from '@core/composables/use-price';

const props = defineProps<{
  product: Product | ESProductHit;
  mode?: AddToCartMode;
  index?: number;
  pageNumber?: number;
  presetQuantity?: boolean;
  onDrawer?: boolean;
  initialVariantId?: number | null;
  /** Quick-view redesign only: renders the running total next to the cart icon. */
  priceBreakdown?: PriceBreakdown;
  showPrice?: boolean;
}>();

const emit = defineEmits<{
  addToCartClicked: [event: Event];
  addedToCart: [payload: unknown];
  variantAddedToCart: [payload: unknown];
  variantModalOpened: [];
  variantModalClosed: [];
  selectVariant: [variant: Variant | undefined];
}>();

const { t } = useI18n();
const { openAuthPopin } = useAuthPopin();
const { updateItemQuantity, ensureCart } = useCart();

const productRef = computed(() => props.product);
const modeRef = computed(() => props.mode ?? 'add');

const {
  quantity,
  confirmation,
  isLoading,
  isMultiVariant,
  isOutOfStock,
  unitMultiplier,
  userIsRetailer,
  userIsBrand,
  canGetWholesalePrice,
  addToCart,
  cleanup,
} = useAddToCart(productRef, modeRef, props.presetQuantity ?? false);

const { getConvertedPrice, formatPrice } = usePrice();
const priceLabel = ref('');
let priceLabelRequestId = 0;
watch(
  [quantity, () => props.priceBreakdown],
  async ([newQuantity, priceBreakdown]) => {
    const requestId = ++priceLabelRequestId;
    if (!priceBreakdown) {
      priceLabel.value = '';
      return;
    }
    const total = calculateTotalPrice(priceBreakdown.finalPrice, newQuantity / unitMultiplier.value, unitMultiplier.value);
    const formatted = total.amount === 0 ? '' : formatPrice(await getConvertedPrice(total));
    // Discard a stale response: a later quantity/price change may have already resolved first.
    if (requestId === priceLabelRequestId) {
      priceLabel.value = formatted;
    }
  },
  { immediate: true }
);

const containerRef = useTemplateRef<HTMLElement>('container');
const multiVariantRef = useTemplateRef<InstanceType<typeof QatcMultiVariant>>('multiVariantRef');
const missingVatModal = useTemplateRef<InstanceType<typeof AkModal>>('missingVatModal');

const selectedVariant = ref<Variant | undefined>();
const selectedQuantity = ref(0);
const isVariantLoading = ref(false);
const variantConfirmation = ref(false);
let variantConfirmationTimer: ReturnType<typeof setTimeout> | null = null;

const { triggerFly } = useFlyToCart();

const isInlineAddDisabled = computed(() => !selectedVariant.value || isVariantLoading.value || userIsBrand.value);

const userGuard = (): boolean => {
  if (!userIsRetailer.value) {
    openAuthPopin(RetailerFormType.Register);
    return false;
  }
  return true;
};

const handleAddToCart = async (event: Event) => {
  emit('addToCartClicked', event);
  if (!userGuard()) {return;}
  const payload = await addToCart(() => missingVatModal.value?.openModal());
  if (payload) {
    emit('addedToCart', payload);
    if (containerRef.value) {triggerFly(containerRef.value);}
  }
};

const handleSelectVariant = () => {
  if (!userGuard()) {return;}
  emit('variantModalOpened');
  multiVariantRef.value?.openModal();
};

const handleVariantSelected = (payload: { variant: Variant | undefined; quantity: number }) => {
  selectedVariant.value = payload.variant;
  selectedQuantity.value = payload.quantity;
  emit('selectVariant', payload.variant);
};

const handleInlineAddToCart = async () => {
  if (!selectedVariant.value?.uuid || isVariantLoading.value) {return;}
  if (!userGuard()) {return;}
  isVariantLoading.value = true;
  try {
    await ensureCart();
    await updateItemQuantity(selectedVariant.value.uuid, selectedQuantity.value, true);
    if (containerRef.value) {triggerFly(containerRef.value);}
    variantConfirmation.value = true;
    variantConfirmationTimer = setTimeout(() => {
      variantConfirmation.value = false;
      variantConfirmationTimer = null;
    }, 2000);
    emit('variantAddedToCart', {
      brand: (props.product as Product).brand,
      option: (props.product as Product).options?.find((o) => o.sku === selectedVariant.value?.sku),
      variant: selectedVariant.value,
      quantity: selectedQuantity.value,
    });
  } finally {
    isVariantLoading.value = false;
  }
};

onUnmounted(() => {
  cleanup();
  if (variantConfirmationTimer) {clearTimeout(variantConfirmationTimer);}
});

const shouldRender = computed(() => userIsRetailer.value && canGetWholesalePrice.value && !isOutOfStock.value);
</script>

<template>
  <div
    v-if="shouldRender"
    ref="container"
    class="qatc-container"
    data-testid="addToCartContainer"
  >
    <template v-if="modeRef !== 'update'">
      <QatcSingleVariant
        v-if="!isMultiVariant"
        v-model:quantity="quantity"
        :unit-multiplier="unitMultiplier"
        :is-loading="isLoading"
        :user-is-brand="userIsBrand"
        :confirmation="confirmation"
        :product-id="product.id"
        :preset-quantity="presetQuantity"
        :on-drawer="onDrawer"
        :price-label="showPrice ? priceLabel : ''"
        @add-click="handleAddToCart"
      />
      <!-- Inline variant form for PDP/drawer -->
      <VariantForm
        v-else-if="presetQuantity"
        :product="(product as Product)"
        :constrain-width="true"
        :on-drawer="onDrawer"
        :initial-variant-id="initialVariantId"
        class="qatc-container__inline-variants"
        @selected-variant="handleVariantSelected"
      >
        <AkButton
          v-if="!variantConfirmation"
          size="lg"
          color="primary"
          symbol="cart"
          :disabled="isInlineAddDisabled"
          data-testid="addToCartButton"
          @click="handleInlineAddToCart"
        >
          <LoaderIcon
            v-if="isVariantLoading"
            :dark="true"
            :small="true"
          />
        </AkButton>
        <AkButton
          v-else
          size="lg"
          color="success"
          data-testid="confirmationButton"
        >
          <AkIcon
            size="md"
            symbol="check"
            color="white"
            class="qatc-container__confirmation-icon"
          />
          {{ t('discovery.qatc.added') }}
        </AkButton>
      </VariantForm>

      <!-- Modal button for catalog -->
      <QatcMultiVariant
        v-else
        ref="multiVariantRef"
        :product-id="product.id"
        :user-is-brand="userIsBrand"
        @select-click="handleSelectVariant"
        @variant-added="emit('variantAddedToCart', $event)"
        @variant-modal-closed="emit('variantModalClosed')"
      />
    </template>

    <!-- Update mode: counter only -->
    <div
      v-else
      class="qatc-container__update"
      data-testid="updateModeContainer"
    >
      <QuantityCounter
        v-model="quantity"
        :step="unitMultiplier"
        :min="0"
        :disabled="userIsBrand"
        :tracking-context="{ source: 'cart', productId: product.id }"
      />
    </div>

    <Teleport to="body">
      <AkModal
        ref="missingVatModal"
        size="xl"
      >
        <VatForm
          from-add-cart
          data-testid="missingVatModal"
          @success="missingVatModal.close()"
          @close="missingVatModal.close()"
        />
      </AkModal>
    </Teleport>

    <slot name="footer" />
  </div>
</template>

<style lang="scss" scoped>
.qatc-container {
  @apply ds-mt-auto;
  z-index: 2;

  &__update {
    @apply ds-mt-3;
  }

  &__inline-variants {
    @apply ds-mt-3;
  }

  &__confirmation-icon {
    @apply ds-mr-2;
  }
}
</style>
